import _, {omit} from 'lodash';

import {move} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createUpdater,
} from '../../../../srcCommon/helpers/updateHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {mapToSignatureIds, selectSortedSignaturesByNamespace} from '../../../helpers/models/signatureHelpers';
import {ActionBase, ActionWithPayload, batch} from '../../../helpers/reduxHelpers';
import {
  ApiSignature,
  ApiSignatures,
  parseSignatureError,
  serializeSignature,
  SignatureModel,
  SignatureTemplateModel,
} from '../../../models/api/signatureModel';
import {deleteAsync, fetchApiAsync, postApiAsync, putApiAsync} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {SignaturesError} from '../../../settings/signatures/signaturesHelpers';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {optimisticUpdateAsyncAction} from '../../optimisticUpdates/optimisticUpdatesActions';
import {getCompany, getCompanyUrl} from '../company/companySelectors';
import {updateDataAction} from '../dataGroupsActions';
import {getOptimisticData} from '../dataSelectors';
import {DataState} from '../dataState';
import {getSignatureById, getSignatures} from './signaturesSelectors';

/*
 * Action types.
 */

export enum SignaturesActionsEnum {
  SIGNATURE_UPDATED = '@@data/signatures/SIGNATURE_UPDATED',
  SIGNATURE_DELETED = '@@data/signatures/SIGNATURE_DELETED',
  SIGNATURES_MARK_AS_STALE = '@@data/signatures/SIGNATURE_MARK_AS_STALE',
}

/*
 * Types.
 */

export interface SignatureUpdatedAction extends ActionWithPayload<SignatureModel> {
  type: SignaturesActionsEnum.SIGNATURE_UPDATED;
}

export interface SignatureDeletedAction extends ActionWithPayload<number> {
  type: SignaturesActionsEnum.SIGNATURE_DELETED;
}

export interface SignatureMarkAsStaleAction extends ActionBase {
  type: SignaturesActionsEnum.SIGNATURES_MARK_AS_STALE;
}

/*
 * Action creators.
 */

export function fetchSignaturesForNamespaceAsyncAction(
  namespace: string,
): ThunkAsyncAction<ReadonlyArray<SignatureModel>> {
  return async (dispatch, getState) => {
    const company = getCompany(getState());
    const fetchSignaturesUrl = `${company.url}/namespaces/${namespace}/signatures`;

    try {
      const signatures = await fetchApiAsync(ApiSignatures, fetchSignaturesUrl);

      dispatch(updateDataAction({signatures}));
      return signatures;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function updateSignature(newSignature: SignatureModel): ThunkAction {
  return (dispatch, getState) => {
    // Find the existing signature.
    const signatures = getSignatures(getState());
    const existingSignature = signatures[newSignature.id];

    // No need to do anything if the new signature is older.
    if (existingSignature && existingSignature.updatedAt > newSignature.updatedAt) {
      return null;
    }

    const action: SignatureUpdatedAction = {
      type: SignaturesActionsEnum.SIGNATURE_UPDATED,
      payload: {...existingSignature, ...newSignature},
    };
    return dispatch(action);
  };
}

export function signatureDeletedAction(signatureId: number): ThunkAction {
  return (dispatch, getState) => {
    // If we don't have this signature, nothing to do.
    const signatures = getSignatures(getState());
    const existingSignature = signatures[signatureId];
    if (!existingSignature) {
      return null;
    }

    const action: SignatureDeletedAction = {
      type: SignaturesActionsEnum.SIGNATURE_DELETED,
      payload: signatureId,
    };
    return dispatch(action);
  };
}

function moveSignatureWithApiAction(
  signatureIds: ReadonlyArray<number>,
  namespace: string,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    try {
      const company = getCompany(getState());
      const updateSignaturesUrl = `${company.url}/namespaces/${namespace}/signatures`;
      const updatedSignatures = await putApiAsync(ApiSignatures, updateSignaturesUrl, signatureIds);

      dispatch(updateDataAction({signatures: updatedSignatures}));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw new SignaturesError(parseSignatureError(error));
    }
  };
}

export function moveSignatureAction(
  signatureId: number,
  offset: number,
  shiftPressed: boolean,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Find the existing signature.
    const signature = getSignatureById(getState(), signatureId);
    if (signature) {
      const {namespace} = signature;
      const optimisticData = getOptimisticData(getState());
      const signatures = [...selectSortedSignaturesByNamespace(optimisticData, namespace)];
      const signatureIndex = signatures.findIndex((s) => s.id === signatureId);
      let newIndex = -1;
      if (shiftPressed) {
        if (offset > 0) {
          newIndex = signatures.length - 1;
        } else {
          newIndex = 0;
        }
      } else {
        newIndex = signatureIndex + offset;
        if (newIndex < 0 || newIndex >= signatures.length) {
          return;
        }
      }

      const signatureIds = move(mapToSignatureIds(signatures), newIndex, signatureId);
      dispatch(moveSignatureWithApiAction(signatureIds, namespace));
    }
  };
}

export function moveSignaturePositionAction(signatureId: number, target: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const signature = getSignatureById(getState(), signatureId);

    if (signature) {
      const {namespace} = signature;
      const optimisticData = getOptimisticData(getState());
      const signatures = [...selectSortedSignaturesByNamespace(optimisticData, namespace)];

      const signatureIds = move(mapToSignatureIds(signatures), target - 1, signatureId);
      dispatch(moveSignatureWithApiAction(signatureIds, namespace));
    }
  };
}

export function createSignatureAsyncAction(newSignature: SignatureTemplateModel): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const optimisticData = getOptimisticData(getState());
    const company = getCompany(getState());

    const signatures = selectSortedSignaturesByNamespace(optimisticData, newSignature.namespace);
    const signatureWithMaxOrder = _(signatures).maxBy((signature) => signature.sortOrder);
    const newSortOrder = signatureWithMaxOrder ? signatureWithMaxOrder.sortOrder + 1 : 1;
    const signature = {...serializeSignature(newSignature), sort_order: newSortOrder};

    try {
      const createSignaturesUrl = `${company.url}/namespaces/${signature.namespace}/signatures`;
      const createdSignature = await postApiAsync(ApiSignature, createSignaturesUrl, signature);

      dispatch(updateDataAction({signatures: [createdSignature]}));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw new SignaturesError(parseSignatureError(error));
    }
  };
}

export function updateSignatureAction(
  signatureId: number,
  signature: SignatureTemplateModel,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const optimisticUpdater = createObjectPropertyUpdater<DataState, 'signatures'>(
      'signatures',
      createObjectPropertyUpdater(
        'byId',
        createResourceMapValueUpdater(
          signatureId,
          createUpdater<SignatureModel>((currentSignature) => ({
            ...currentSignature,
            ...signature,
          })),
        ),
      ),
    );

    await dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () => {
        const company = getCompany(getState());
        const unmodifiedSignature = getSignatureById(getState(), signatureId);
        if (!unmodifiedSignature) {
          return undefined;
        }

        try {
          const updateSignaturesUrl = `${company.url}/signatures/${signatureId}`;
          const combinedSignature = serializeSignature({
            ...unmodifiedSignature,
            ...signature,
          });

          const updatedSignature = await putApiAsync(ApiSignature, updateSignaturesUrl, combinedSignature);

          return updateDataAction({signatures: [updatedSignature]});
        } catch (error) {
          if (!(error instanceof HttpError)) {
            reportToBugsnag(error);
          }
          throw new SignaturesError(parseSignatureError(error));
        }
      }),
    );
  };
}

export function deleteSignatureAsyncAction(signatureId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const optimisticUpdater = createObjectPropertyUpdater<DataState, 'signatures'>(
      'signatures',
      createUpdater((signatures) => {
        const {byId, deletedIds} = signatures;
        return {
          ...signatures,
          deletedIds: deletedIds.union([signatureId]),
          byId: omit(byId, [signatureId]),
        };
      }),
    );

    await dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () => {
        const company = getCompany(getState());

        try {
          const signaturesUrl = `${company.url}/signatures/${signatureId}`;
          await deleteAsync(signaturesUrl);
          return {
            type: SignaturesActionsEnum.SIGNATURE_DELETED,
            payload: signatureId,
          };
        } catch (error) {
          if (!(error instanceof HttpError)) {
            reportToBugsnag(error);
          }
          throw new SignaturesError(parseSignatureError(error));
        }
      }),
    );
  };
}

export function deleteSignaturesAsyncAction(signatureIds: ReadonlyArray<number>): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    // We have no multi delete endpoint, this issues multiple events.
    const deleteRequests = signatureIds.map((signatureId) =>
      deleteAsync(`${companyUrl}/signatures/${signatureId}`),
    );

    try {
      await Promise.all(deleteRequests);
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
    }

    batch(() => {
      signatureIds.forEach((signatureId) => dispatch(signatureDeletedAction(signatureId)));
    });
  };
}

export function markSignaturesAsStaleAction(): SignatureMarkAsStaleAction {
  return {
    type: SignaturesActionsEnum.SIGNATURES_MARK_AS_STALE,
  };
}

/*
 * Union type.
 */

export type SignaturesAction = SignatureUpdatedAction | SignatureDeletedAction | SignatureMarkAsStaleAction;
